import { baseUrl } from './api.js';

/**
 * Krok GCAL1 — Nastavení → Integrace → „Google kalendář" a obsazenost pro
 * obrazovku kalendáře. Stejný vzor jako `kn-settings-api.ts`: raw `fetch`,
 * protože routy jsou nové a ještě nejsou v generovaném `@reos/api-client`.
 *
 * Přihlašovací údaje účtu služby sem NIKDY nechodí — ven jde jen adresa
 * (`serviceAccountEmail`), se kterou makléř kalendář nasdílí.
 */

export interface GoogleCalendarSettingsDto {
  /** Server má přihlašovací údaje účtu služby; `false` = funkce vypnutá. */
  readonly available: boolean;
  readonly serviceAccountEmail: string | null;
  readonly calendarId: string | null;
  readonly verifiedAt: string | null;
  /** Klíč hlášky poslední chyby (`crm.googleCalendar.error.*`). */
  readonly lastError: string | null;
}

export interface GoogleCalendarVerifyDto {
  readonly ok: boolean;
  readonly messageKey: string | null;
}

export interface GoogleBusyInterval {
  readonly startsAt: string;
  readonly endsAt: string;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export async function getGoogleCalendarSettings(): Promise<GoogleCalendarSettingsDto> {
  return json(await fetch(`${baseUrl}/v1/crm/google-calendar`, { credentials: 'include' }));
}

export async function saveGoogleCalendarId(calendarId: string): Promise<GoogleCalendarSettingsDto> {
  return json(
    await fetch(`${baseUrl}/v1/crm/google-calendar`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ calendarId }),
    }),
  );
}

export async function removeGoogleCalendarLink(): Promise<void> {
  const res = await fetch(`${baseUrl}/v1/crm/google-calendar`, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function verifyGoogleCalendar(): Promise<GoogleCalendarVerifyDto> {
  return json(
    await fetch(`${baseUrl}/v1/crm/google-calendar/verify`, { method: 'POST', credentials: 'include' }),
  );
}

/**
 * Obsazené bloky z Googlu v okně. Selhání se NEVYHAZUJE dál jako chyba
 * obrazovky — kalendář se musí nakreslit i když cizí služba nefunguje
 * (fail-open, stejné pravidlo jako na serveru).
 */
export async function listGoogleBusy(from: string, to: string): Promise<GoogleBusyInterval[]> {
  try {
    const res = await fetch(
      `${baseUrl}/v1/crm/google-calendar/busy?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      { credentials: 'include' },
    );
    if (!res.ok) return [];
    return (await res.json() as { items: GoogleBusyInterval[] }).items;
  } catch {
    return [];
  }
}
